import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BedDouble, Bath, Ruler, Box, KeyRound } from "lucide-react";
import { api, asset, money, applyTheme } from "@/lib/api";
import { SiteHeader } from "@/components/Brand";
import { LeadCTA } from "@/components/LeadForms";
import { CtaChooser } from "@/components/CtaChooser";
import { setViewerTerms, terms, sellsHomes, ctaLinks } from "@/lib/industry";

export default function BuilderPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/public/builders/${slug}`).then((r) => { setData(r.data); applyTheme(r.data.builder); });
  }, [slug]);
  if (!data) return <div className="min-h-screen" />;
  const { builder, developments, house_types, scans = [], move_in_ready = [] } = data;
  setViewerTerms(builder, ctaLinks(builder));
  const t = terms(builder);
  const sells = sellsHomes(builder);
  return (
    <div className="min-h-screen pb-28">
      <CtaChooser />
      <SiteHeader builder={builder} right={<LeadCTA builderId={builder.id} className="hidden md:flex" />} />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-ba">{builder.tagline}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl">{sells ? "Homes" : t.homes} by {builder.name}</h1>
      </section>
      {developments.length > 0 && <section className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-8 md:grid-cols-2">
        {developments.map((d) => (
          <Link key={d.id} to={`/d/${d.id}`} className="group rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-shadow" data-testid={`development-card-${d.id}`}>
            <div className="aspect-[16/9] overflow-hidden"><img src={asset(d.hero_url || d.site_plan_url)} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" /></div>
            <div className="p-6">
              <h3 className="font-serif text-2xl">{d.name}</h3>
              <p className="text-sm text-slate-500">{d.location}</p>
              <p className="mt-3 text-sm text-slate-700">{d.available} lots available{d.from_price ? ` · from ${money(d.from_price)}` : ""}</p>
            </div>
          </Link>
        ))}
      </section>}
      {move_in_ready.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20" data-testid="move-in-ready-section">
          <p className="text-xs uppercase tracking-[0.3em] text-sky-600 flex items-center gap-2"><KeyRound className="w-4 h-4" />Quick move-in</p>
          <h2 className="font-serif text-3xl font-semibold mt-2 mb-8">Move-in ready homes</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {move_in_ready.map((p) => (
              <Link key={p.id} to={`/view/${p.house_type_id}?plot=${p.id}`} className="rounded-2xl bg-white border overflow-hidden hover:shadow-xl transition-shadow" data-testid={`move-in-ready-card-${p.id}`}>
                <img src={asset(p.photos?.[0])} alt="" className="aspect-[4/3] w-full object-cover" />
                <div className="p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-500">{p.development_name} · Lot {p.plot_number}</p>
                  <h3 className="font-serif text-xl mt-1">{p.house_type_name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{p.beds} bed · {p.baths} bath · {p.sqft} sq ft</p>
                  <p className="font-serif text-xl mt-3">{money(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      {scans.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20" data-testid="virtual-tours-section">
          <p className="text-xs uppercase tracking-[0.3em] text-ba flex items-center gap-2"><Box className="w-4 h-4" />Step inside</p>
          <h2 className="font-serif text-3xl font-semibold mt-2 mb-8">Virtual tours</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {scans.map((s) => (
              <Link key={s.id} to={`/view/${s.id}`} className="rounded-2xl bg-white border p-6 hover:shadow-xl transition-shadow" data-testid={`virtual-tour-card-${s.id}`}>
                <span className="w-11 h-11 rounded-2xl bg-bp text-white grid place-items-center"><Box className="w-5 h-5" /></span>
                <h3 className="font-serif text-xl mt-4">{s.name}</h3>
                {s.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{s.description}</p>}
                <p className="mt-4 text-sm font-semibold text-bp">Take the 3D tour</p>
              </Link>
            ))}
          </div>
        </section>
      )}
      {house_types.length > 0 && <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <h2 className="font-serif text-3xl font-semibold mb-8">{!sells ? t.homes : developments.length ? "House types" : "Our homes"}</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {house_types.map((h) => (
            <div key={h.id} className="rounded-2xl bg-white overflow-hidden border" data-testid={`house-type-card-${h.id}`}>
              <img src={asset(h.photos?.[0])} alt={h.name} className="aspect-[4/3] w-full object-cover" />
              <div className="p-5">
                <h3 className="font-serif text-xl">{h.name}</h3>
                {!sells && h.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{h.description}</p>}
                {sells && <><div className="flex gap-4 text-sm text-slate-600 mt-2">
                  <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{h.beds}</span>
                  <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{h.baths}</span>
                  <span className="flex items-center gap-1"><Ruler className="w-4 h-4" />{h.sqft} sq ft</span>
                </div>
                <p className="font-serif text-xl mt-3">from {money(h.base_price)}</p></>}
                <Link to={`/view/${h.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-bp" data-testid={`house-type-tour-link-${h.id}`}><Box className="w-4 h-4" />Explore in 3D</Link>
              </div>
            </div>
          ))}
        </div>
      </section>}
      <div className="md:hidden fixed bottom-0 inset-x-0 p-3 glass z-30"><LeadCTA builderId={builder.id} className="justify-center" /></div>
    </div>
  );
}
